import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { permissionObj } from '../../../../../Helpers/permission';
import routes from '../../../../../Routes/routes';
import { CreateDish, CreateDishImage } from '../../../../../reduxToolkit/FoodieDishes/DishesApi';
import { toast } from 'react-toastify';
import { base64ToBlob } from '../../../../../utils/Base64ToBlob';
import defaultImage from '../../../../../assets/IconsButtons/ic-ingredient-card.svg';
import { PHOTO_PICKER_MODEL } from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import { DISH_MODEL } from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import { errorMsg } from '../../../../../Apis/functions';
/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle : Frontend
 * @Email : sgarcia@ibl.mx
 *
 */
const CreateDishApp = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // UI and form state
    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [item, setItem] = useState({ ...DISH_MODEL });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [selectedAllergens, setSelectedAllergens] = useState([]);
    const [selectedDietTypes, setSelectedDietTypes] = useState([]);
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [isValid, setIsValid] = useState(true);
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL, preview: defaultImage });
    // Dish base data
    const [dishData, setDishData] = useState({
        name: '',
        itemCode: '',
        description: '',
    });
    // Set translated step names
    useLayoutEffect(() => {
        setSteps([t("set_up"), t("ingredient"), t("diet_restrictions"), t("upload_photo"), t("summary")]);
    }, [permission, t]);
    // Check user permissions and redirect if not allowed
    useEffect(() => {
        if (DEV) return;

        if (
            (!id && !permission?.includes(permissionObj?.WEB_FOODIE_ITEM_CREATE)) ||
            (id && !permission?.includes(permissionObj?.WEB_FOODIE_ITEM_UPDATE))
        ) {
            navigate(routes.forbidden);
        }
    }, [DEV, permission, id]);
    /**
     * Validates the current step before allowing user to proceed.
     * Prevents progress if required fields are empty.
     */
    const handleStepSetUp = () => {
        switch (activeStep) {
            case 0:
                if (!dishData.name || !dishData.itemCode) {
                    setEmptyFields(true);
                     toast.warn(t('please_fill_all_fields'));
                    return false;
                }
                break;
            case 1:
                if (selectedIngredients.length === 0) {
                    setEmptyFields(true);
                     toast.warn(t('please_add_at_least_one_ingredient'));
                    return false;
                }
                break;
            case 2:
                if (selectedAllergens.length === 0) {
                    setEmptyFields(true);
                     toast.warn(t('please_add_allergen'));
                    return false;
                }
                break;
            default:
                break;
        }

        setEmptyFields(false);
        return true;
    };

    // Check if empty fields are still empty once user edits form
   useEffect(() => {
    if (!emptyFields) return;

    switch (activeStep) {
        case 0:
            if (dishData.name && dishData.itemCode) {
                setEmptyFields(false);
            }
            break;
        case 1:
            if (selectedIngredients.length > 0) {
                setEmptyFields(false);
            }
            break;
        case 2:
            if (selectedAllergens.length > 0) {
                setEmptyFields(false);
            }
            break;
        default:
            break;
    }
}, [dishData, selectedIngredients, selectedAllergens, activeStep, emptyFields]);

    /**
     * Handles the main create/update dish action.
     * Sends dish data and image (if selected) to the backend.
     */

    const handleCreate = () => {
        // Validate mandatory fields in the form
        if (!item.isValidSetUpData()) {
            setEmptyFields(true); // Show empty fields error
            return;
        }
        // Prepare data to send to API
        let aux = {
            ...item,
            ingredients: selectedIngredients.map((ingredient) => ({
                ingredientId: ingredient.ingredientId || ingredient.id,
                quantity: ingredient.unit,
            })),
            allergenIds: selectedAllergens.map((item) => item.id),
            dietTypeIds: selectedDietTypes.map((item) => item.id),
        };
        let body = aux.toRegister();

        dispatch(CreateDish(body)).then(
            ({
                payload: {
                    data: { data, code, message },
                },
            }) => {
                if (data?.id) {
                    const newImageToUpload =
                        photoPicker.fileSelected !== null
                            ? photoPicker.fileSelected
                            : photoPicker?.selfieSrc != null
                              ? base64ToBlob(photoPicker.selfieSrc)
                              : null;
                    if (!newImageToUpload || newImageToUpload == null) {
                        toast.success(t('has_been_created_successfully'));
                        navigate(routes.foodie.list);
                        return;
                    }
                    dispatch(
                        CreateDishImage({
                            imgData: { foodieItemId: data.id },
                            image: newImageToUpload,
                        }),
                    ).then(({ payload }) => {
                        const { data } = payload?.data || {};
                        if (data?.id) {
                            toast.success(t('has_been_created_successfully'));
                            navigate(routes.foodie.list);
                        } else {
                            toast.warn(t('image_upload_failed'));
                        }
                    });
                } else errorMsg (code);
            },
        );
    };
    return {
        t,
        disabled,
        emptyFields,
        activeStep,
        setActiveStep,
        steps,
        setSteps,
        id,
        DEV,
        permission,
        navigate,
        dishData,
        setDishData,
        handleStepSetUp,
        selectedAllergens,
        setSelectedAllergens,
        selectedDietTypes,
        setSelectedDietTypes,
        selectedIngredients,
        setSelectedIngredients,
        handleCreate,
        photoPicker,
        setPhotoPicker,
        item,
        setItem,
        handleChange,
        setEmptyFields,
        isValid,
        setIsValid,
    };
};

export default CreateDishApp;
